import React, { useEffect, useState } from 'react';
import Icon from '../AppIcon';

/**
 * ErrorToast — displays a stack of error/warning/info toasts in the bottom-right corner.
 *
 * Props:
 *   toasts: Array<{ id, message, type? ('error'|'warning'|'info') }>
 *   onDismiss: (id) => void
 *   autoDismissMs?: number  (default 5000, 0 = no auto-dismiss)
 */
const ICONS = {
  error: { name: 'AlertCircle', color: 'var(--color-destructive, #ef4444)' },
  warning: { name: 'AlertTriangle', color: '#f59e0b' },
  info: { name: 'Info', color: 'var(--color-primary)' },
};

const BG = {
  error: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
};

const ToastItem = ({ toast, onDismiss, autoDismissMs }) => {
  const type = toast?.type || 'error';
  const icon = ICONS?.[type] || ICONS?.error;
  const bg = BG?.[type] || BG?.error;

  useEffect(() => {
    if (!autoDismissMs) return;
    const timer = setTimeout(() => onDismiss(toast?.id), autoDismissMs);
    return () => clearTimeout(timer);
  }, [toast?.id, autoDismissMs, onDismiss]);

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border shadow-md text-sm max-w-sm w-full ${bg} animate-fade-in`}
      role="alert"
    >
      <Icon name={icon?.name} size={16} color={icon?.color} className="mt-0.5 shrink-0" />
      <span className="flex-1 leading-snug">{toast?.message}</span>
      <button
        onClick={() => onDismiss(toast?.id)}
        className="shrink-0 opacity-60 hover:opacity-100 transition-opacity ml-1"
        aria-label="Fermer"
      >
        <Icon name="X" size={14} />
      </button>
    </div>
  );
};

const ErrorToast = ({ toasts = [], onDismiss, autoDismissMs = 5000 }) => {
  if (!toasts?.length) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 items-end pointer-events-none">
      {toasts?.map(toast => (
        <div key={toast?.id} className="pointer-events-auto">
          <ToastItem
            toast={toast}
            onDismiss={onDismiss}
            autoDismissMs={autoDismissMs}
          />
        </div>
      ))}
    </div>
  );
};

export default ErrorToast;
